import React, { lazy, Suspense } from "react";
import { Navigate } from "react-router-dom";
import LoadingSpinner from "../Components/Common/LoadingSpinner";

// Lazy load components for better performance
const Starter = lazy(() => import("../pages/Pages/Starter/Starter.jsx"));
const Basic404 = lazy(() => import("../pages/AuthenticationInner/Errors/Basic404.jsx"));
const Cover404 = lazy(() => import("../pages/AuthenticationInner/Errors/Cover404.jsx"));
const Alt404 = lazy(() => import("../pages/AuthenticationInner/Errors/Alt404.jsx"));
const Error500 = lazy(() => import("../pages/AuthenticationInner/Errors/Error500.jsx"));
const LandingPage = lazy(() => import("../pages/Landing/index.jsx"));
const TermsAndConditions = lazy(() => import("../pages/TermsAndConditions/index.jsx"));
const PrivacyPolicy = lazy(() => import("../pages/PrivacyPolicy/index.jsx"));
const TermsOfService = lazy(() => import("../pages/TermsOfService/index.jsx"));

// Authentication pages
const Login = lazy(() => import("../pages/Authentication/Login.jsx"));
const ForgetPasswordPage = lazy(() => import("../pages/Authentication/ForgetPassword.jsx"));
const Logout = lazy(() => import("../pages/Authentication/Logout.jsx"));
const Register = lazy(() => import("../pages/Authentication/Register.jsx"));

// Main pages
const Customers = lazy(() => import("../pages/Customers/index.jsx"));
const Products = lazy(() => import("../pages/Products/index.jsx"));
const Settings = lazy(() => import("../pages/Settings/index.jsx"));
const Users = lazy(() => import("../pages/Users/index.jsx"));
const Orders = lazy(() => import("../pages/Orders/index.jsx"));
const Dashboard = lazy(() => import("../pages/Dashboard/index.jsx"));
const CalendarPage = lazy(() => import("../pages/Calendar/index.jsx"));

// Form pages
const AddProduct = lazy(() => import("../pages/Products/AddProduct.jsx"));
const ProductDetail = lazy(() => import("../pages/Products/ProductDetail.jsx"));
const CreateOrder = lazy(() => import("../pages/Orders/CreateOrder.jsx"));
const EditOrder = lazy(() => import("../pages/Orders/EditOrder.jsx"));
const FormUser = lazy(() => import("../pages/Users/FormUser.jsx"));
const OrderDetail = lazy(() => import("../pages/Orders/OrderDetail.jsx"));
const CustomerForm = lazy(() => import("../pages/Customers/CustomerForm.jsx"));
const FormCustomer = lazy(() => import("../pages/Customers/FormCustomer.jsx"));
const ProductAvailabilityTest = lazy(() => import("../pages/Orders/components/ProductAvailabilityTest.jsx"));

// Loading component for Suspense
const RouteLoadingSpinner = () => <LoadingSpinner />;

// Wrap components with Suspense
const withSuspense = (Component) => {
  const SuspenseWrapper = (props) => (
    <Suspense fallback={<RouteLoadingSpinner />}>
      <Component {...props} />
    </Suspense>
  );
  SuspenseWrapper.displayName = `withSuspense(${Component.displayName || Component.name || 'Component'})`;
  return SuspenseWrapper;
};

// Dashboard routes
const dashboardRoutes = [
  { path: "/dashboard", component: withSuspense(Dashboard) },
];

// Customer management routes
const customerRoutes = [
  { path: "/customers", component: withSuspense(Customers) },
  { path: "/add-customer", component: withSuspense(CustomerForm) },
  { path: "/edit-customer/:id", component: withSuspense(CustomerForm) },
  { path: "/customer-detail/:id", component: withSuspense(FormCustomer) },
];

// Product management routes
const productRoutes = [
  { path: "/products", component: withSuspense(Products) },
  { path: "/add-product", component: withSuspense(AddProduct) },
  { path: "/edit-product/:id", component: withSuspense(AddProduct) },
  { path: "/product-detail/:id", component: withSuspense(ProductDetail) },
];

// Settings routes
const settingsRoutes = [
  { path: "/settings", component: withSuspense(Settings) },
];

// User management routes
const userRoutes = [
  { path: "/users", component: withSuspense(Users) },
  { path: "/add-user", component: withSuspense(FormUser) },
  { path: "/edit-user/:id", component: withSuspense(FormUser) },
];

// Order management routes
const orderRoutes = [
  { path: "/orders", component: withSuspense(Orders) },
  { path: "/create-order", component: withSuspense(CreateOrder) },
  { path: "/edit-order/:id", component: withSuspense(EditOrder) },
  { path: "/order-detail/:id", component: withSuspense(OrderDetail) },
  { path: "/test-availability", component: withSuspense(ProductAvailabilityTest) },
];

// Calendar routes
const calendarRoutes = [
  { path: "/calendar", component: withSuspense(CalendarPage) },
];

// Utility routes
const utilityRoutes = [
  { path: "/pages-starter", component: withSuspense(Starter) },
];

// Legal routes
const legalRoutes = [
  { path: "/terms", component: withSuspense(TermsAndConditions) },
  { path: "/privacy-policy", component: withSuspense(PrivacyPolicy) },
  { path: "/chinh-sach-quyen-rieng-tu", component: withSuspense(PrivacyPolicy) },
  { path: "/terms-of-service", component: withSuspense(TermsOfService) },
  { path: "/dieu-khoan-dich-vu", component: withSuspense(TermsOfService) },
];

const authProtectedRoutes = [
  // Dashboard
  ...dashboardRoutes,
  
  // Customer management
  ...customerRoutes,
  
  // Product management
  ...productRoutes,
  
  // Settings
  ...settingsRoutes,
  
  // User management
  ...userRoutes,
  
  // Order management
  ...orderRoutes,
  
  // Calendar
  ...calendarRoutes,
  
  // Utility pages
  ...utilityRoutes,
  
  // Legal pages
  ...legalRoutes,
];

const publicRoutes = [
  // Landing page
  { path: "/", component: withSuspense(LandingPage) },
  
  // Authentication routes
  { path: "/login", component: withSuspense(Login) },
  { path: "/logout", component: withSuspense(Logout) },
  { path: "/forgot-password", component: withSuspense(ForgetPasswordPage) },
  { path: "/register", component: withSuspense(Register) },

  // Legal routes
  { path: "/terms", component: withSuspense(TermsAndConditions) },
  { path: "/privacy-policy", component: withSuspense(PrivacyPolicy) },
  { path: "/chinh-sach-quyen-rieng-tu", component: withSuspense(PrivacyPolicy) },
  { path: "/terms-of-service", component: withSuspense(TermsOfService) },
  { path: "/dieu-khoan-dich-vu", component: withSuspense(TermsOfService) },

  // Error pages
  { path: "/auth-404-basic", component: withSuspense(Basic404) },
  { path: "/auth-404-cover", component: withSuspense(Cover404) },
  { path: "/auth-404-alt", component: withSuspense(Alt404) },
  { path: "/auth-500", component: withSuspense(Error500) },
];

export { authProtectedRoutes, publicRoutes };
